import { FormInstance, Spin, message } from "antd";
import React, { RefObject } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  APIError,
  AssetAllocationItem,
  FormFieldConfig,
} from "../helpers/shared-interfaces";
import Card from "../components/Card";
import BaseForm from "../components/Form/BaseForm";
import {
  generateDisplayErrorMessage,
  handleValidationErrors,
  mapAPIErrorToFormError,
  request,
  responseHandler,
} from "../helpers/utilities";
import * as Sentry from "@sentry/browser";
import { AxiosResponse } from "axios";
import {
  GET_ASSET_ALLOCATION_ITEM,
  UPDATE_ASSET_ALLOCATION_ITEM,
} from "../gql/economicIndicators";

interface RouteParams {
  id: string;
}

interface State {
  id: string;
  isLoading: boolean;
  assetAllocationItem?: AssetAllocationItem;
}

export default class EditAssetAllocationItem extends React.Component<
  RouteComponentProps<RouteParams>,
  State
> {
  reference: RefObject<FormInstance>;
  constructor(props: Readonly<RouteComponentProps<RouteParams>>) {
    super(props);
    this.state = {
      id: "",
      isLoading: true,
    };
    this.reference = React.createRef<FormInstance>();
    this.getAssetAllocationItem = this.getAssetAllocationItem.bind(this);
    this.updateAssetAllocationItem = this.updateAssetAllocationItem.bind(this);
  }

  componentDidMount(): void {
    this.setState({ id: this.props.match.params.id }, (): void => {
      this.getAssetAllocationItem();
    });
  }

  getAssetAllocationItem = (): void => {
    const { id } = this.state;
    this.setState(
      {
        isLoading: true,
      },
      (): void => {
        request({
          query: GET_ASSET_ALLOCATION_ITEM,
          variables: { id },
        }).then(({ data }: AxiosResponse<any>): void => {
          this.setState({ isLoading: false });
          if (!data.errors) {
            const assetAllocationItem = data.data?.assetAllocationItem;
            this.setState({
              assetAllocationItem: assetAllocationItem,
            });
          } else {
            message.error("Failed to get asset allocation item data");
          }
        });
      },
    );
  };

  setRef = (ref: React.RefObject<FormInstance>): void => {
    this.reference = ref;
  };

  redirectToViewAssetAllocationItemPage(): void {
    this.props.history.push(`/asset-allocation-items/${this.state.id}`);
  }

  async updateAssetAllocationItem(): Promise<void> {
    const { id } = this.state;
    interface Response {
      data?: {
        updateAssetAllocationItem: {
          assetAllocationItem: AssetAllocationItem;
          errors: APIError[];
        };
      };
    }

    if (!this.reference.current) return;
    let data;
    try {
      data = await this.reference.current.validateFields(["name"]);
    } catch (error) {
      handleValidationErrors(error);
      return;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: UPDATE_ASSET_ALLOCATION_ITEM,
        variables: {
          id,
          ...data,
        },
      });

      const { err, handled } = responseHandler<Response>(
        response,
        "updateAssetAllocationItem",
      );

      this.setState({ isLoading: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(generateDisplayErrorMessage(err));
        this.reference.current.setFields(err.map(mapAPIErrorToFormError));
      } else {
        message.success("Asset allocation item updated sucessfully");
        this.redirectToViewAssetAllocationItemPage();
      }
    } catch (error) {
      this.setState({ isLoading: false });

      message.error(`Failed to update asset allocation item - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  }

  render(): JSX.Element | null {
    const { isLoading, assetAllocationItem } = this.state;
    const fields: FormFieldConfig[] = [
      {
        id: "name",
        name: "name",
        rule: "string",
        inputType: "string",
        required: true,
        label: "Name",
        initialValue: assetAllocationItem?.name,
        placeholder: "Name",
        colConfig: {
          span: 7,
        },
      },
      {
        id: "updateAssetAllocationItem",
        name: "updateAssetAllocationItemButton",
        inputType: "button",
        value: "Save",
        required: false,
        className: "base-button",
        elementStyle: { position: "relative", top: "3px" },
        colConfig: {
          span: 24,
        },
        handleClick: this.updateAssetAllocationItem,
      },
    ];

    return (
      <Card header="UPDATE ASSET ALLOCATION ITEM">
        <div>
          <Spin spinning={isLoading}>
            <BaseForm
              layout="vertical"
              reference={this.reference}
              fieldsToHide={[]}
              fields={fields}
            />
          </Spin>
        </div>
      </Card>
    );
  }
}
